/**
 * Our concrete syntax tree: the shape of `jsonc-parser`'s `Node`, restated as our own data.
 *
 * Restated rather than re-exported for the same reason the token numbers are local
 * (ADR-0022): the library's `Node` carries `parent` back-references, which makes it cyclic
 * and therefore awkward to reason about, and it pins us to one scanner. Offsets are
 * half-open UTF-16 code units into the ORIGINAL source, which is what makes span-based
 * editing possible — a tree edit is a `TextEdit`, never a re-serialisation, so key order,
 * `1e10` and 20-digit ids all survive (docs/ARCHITECTURE.md §8.1).
 *
 * Note what this file does NOT do: import `jsonc-parser`. The conversion takes a
 * structural {@link JsoncNodeLike}, so the three files that know the library stay three
 * (ADR-0007) and the traversal helpers below are testable against hand-written trees.
 */

export type JsonNodeKind =
  'object' | 'array' | 'property' | 'string' | 'number' | 'boolean' | 'null';

export interface JsonNode {
  readonly kind: JsonNodeKind;
  readonly from: number;
  readonly to: number;
  /** Members for `object`, elements for `array`, `[key, value]` for `property`. */
  readonly children?: readonly JsonNode[];
  /** Scalars only. The literal text is always recoverable as `source.slice(from, to)`. */
  readonly value?: string | number | boolean | null;
}

/**
 * The shape of `jsonc-parser`'s `Node`, structurally — enough of it to convert, and not one
 * field more.
 *
 * `parent` is deliberately absent: it is what makes the library's tree cyclic, and nothing
 * downstream needs it because {@link walk} hands every visitor its own ancestor chain.
 */
export interface JsoncNodeLike {
  readonly type: JsonNodeKind;
  readonly offset: number;
  readonly length: number;
  readonly value?: unknown;
  readonly children?: readonly JsoncNodeLike[];
}

const CONTAINER_KINDS: ReadonlySet<JsonNodeKind> = new Set<JsonNodeKind>([
  'object',
  'array',
  'property',
]);

/** `object`, `array` and `property` carry children; the four scalar kinds carry a value. */
export function isContainer(node: JsonNode): boolean {
  return CONTAINER_KINDS.has(node.kind);
}

function isJsonScalar(value: unknown): value is string | number | boolean | null {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

function makeNode(source: JsoncNodeLike, children: JsonNode[], sourceLength: number): JsonNode {
  // Clamped, because jsonc 3.3.1 can hand back a span past the end of the text: an object
  // closed only by an empty unterminated block comment (`{/*`) arrives as offset 0,
  // length 4 over a three-unit document. A node whose `to` exceeds the document turns into
  // a `TextEdit` that splices past the end, so the overrun stops here (see `lexer.ts`,
  // which clamps the same bug one layer down).
  const from = Math.min(Math.max(source.offset, 0), sourceLength);
  const to = Math.min(Math.max(source.offset + source.length, from), sourceLength);

  // Containers always carry `children`, empty array included, so a consumer can write
  // `node.children.length` after one `isContainer` check instead of a `?? []` per access.
  if (CONTAINER_KINDS.has(source.type)) {
    return { kind: source.type, from, to, children };
  }

  // A scalar whose value is outside the JSON domain (jsonc never produces one today)
  // keeps its kind and span and simply has no `value` — `source.slice(from, to)` still
  // recovers the literal text, which is what the printer and the repair rules read.
  return isJsonScalar(source.value)
    ? { kind: source.type, from, to, value: source.value }
    : { kind: source.type, from, to };
}

/**
 * Convert a `jsonc-parser` node into our own tree.
 *
 * Iterative, like everything else in this folder: `parseTree` itself survives to depth 3928
 * (ADR-0007), and a recursive converter would be a second, lower ceiling nobody measured.
 *
 * Malformed input keeps producing nodes, because jsonc's tolerant mode does: a property
 * whose value is missing (`{"a":}`) arrives with a single child, and it stays a `property`
 * with a single child here. Consumers must not assume `children.length === 2`.
 *
 * Pass `sourceLength` whenever the tree came from a real parse: it clamps spans the library
 * reports past the end of the document. `parse.ts` always passes it.
 */
export function fromJsoncNode(
  root: JsoncNodeLike,
  sourceLength = Number.MAX_SAFE_INTEGER,
): JsonNode {
  interface Frame {
    readonly source: JsoncNodeLike;
    readonly children: JsonNode[];
    /** Where this node is appended once its children are finished. */
    readonly sink: JsonNode[];
    next: number;
  }

  const finished: JsonNode[] = [];
  const stack: Frame[] = [{ source: root, children: [], sink: finished, next: 0 }];

  while (stack.length > 0) {
    const frame = stack[stack.length - 1];
    if (frame === undefined) break;

    const kids = frame.source.children;
    if (kids !== undefined && frame.next < kids.length) {
      const child = kids[frame.next];
      frame.next += 1;
      if (child !== undefined) {
        stack.push({ source: child, children: [], sink: frame.children, next: 0 });
      }
      continue;
    }

    stack.pop();
    frame.sink.push(makeNode(frame.source, frame.children, sourceLength));
  }

  const converted = finished[0];
  // The loop pushes exactly one node into `finished` — the root — before it can end.
  return converted ?? makeNode(root, [], sourceLength);
}

/**
 * Depth-first pre-order traversal. Iterative, for the same reason as everything else here.
 *
 * `parents` is ordered outermost-first: `parents[0]` is always `root` for any node other
 * than the root itself, and `parents.at(-1)` is the node's immediate parent. It is a fresh
 * array on every call, so a visitor may keep it.
 */
export function walk(
  root: JsonNode,
  visit: (node: JsonNode, parents: readonly JsonNode[]) => void,
): void {
  const POP = null;
  const stack: (JsonNode | typeof POP)[] = [root];
  const parents: JsonNode[] = [];

  while (stack.length > 0) {
    const entry = stack.pop();
    if (entry === undefined) break;

    if (entry === POP) {
      parents.pop();
      continue;
    }

    visit(entry, parents.slice());

    const children = entry.children;
    if (children !== undefined && children.length > 0) {
      stack.push(POP);
      parents.push(entry);
      for (let i = children.length - 1; i >= 0; i -= 1) {
        const child = children[i];
        if (child !== undefined) stack.push(child);
      }
    }
  }
}

/** `true` when `offset` falls inside `node`'s half-open span. */
function contains(node: JsonNode, offset: number): boolean {
  return offset >= node.from && offset < node.to;
}

function childAt(node: JsonNode, offset: number): JsonNode | undefined {
  const children = node.children;
  if (children === undefined) return undefined;

  for (const child of children) {
    if (contains(child, offset)) return child;
  }
  return undefined;
}

/**
 * The innermost node whose span contains `offset`, or `undefined` when the offset falls
 * outside the tree — in leading whitespace, past the root, or in the gap a malformed
 * document leaves between two children.
 *
 * The span is half-open: `to` itself is *outside*. `nodeAt(root, node.to)` is therefore the
 * node *after* it, or whatever encloses the gap, never the node itself. That is the same
 * convention as `Token.from`/`Token.to` and as CodeMirror's ranges, so an offset can be
 * carried between the three without adjustment.
 */
export function nodeAt(root: JsonNode, offset: number): JsonNode | undefined {
  if (!contains(root, offset)) return undefined;

  let current = root;
  for (;;) {
    const child = childAt(current, offset);
    if (child === undefined) return current;
    current = child;
  }
}

/**
 * The JSON path of the innermost node at `offset`: property names for objects, 0-based
 * indices for arrays, `[]` for the root and for an offset outside the tree.
 *
 * The semantics match `jsonc-parser`'s own `getNodePath`, deliberately, so a path produced
 * here can be fed to anything that speaks that dialect. Two consequences worth knowing:
 * a **property key** reports the path of its own property (`{"a":1}` at offset 1 → `['a']`,
 * not `[]`), and a **property node itself** reports the path of its enclosing object
 * (offset of the whole `"a":1` → `[]`), because the key is what names it and the property
 * node starts exactly where the key does.
 */
export function pathAt(root: JsonNode, offset: number): readonly (string | number)[] {
  if (!contains(root, offset)) return [];

  const path: (string | number)[] = [];
  let current = root;

  for (;;) {
    const children = current.children;
    if (children === undefined) return path;

    let index = -1;
    let child: JsonNode | undefined;
    for (let i = 0; i < children.length; i += 1) {
      const candidate = children[i];
      if (candidate !== undefined && contains(candidate, offset)) {
        index = i;
        child = candidate;
        break;
      }
    }
    if (child === undefined) return path;

    if (current.kind === 'array') {
      path.push(index);
    } else if (current.kind === 'property') {
      const key = children[0]?.value;
      // A property with a broken key (`{:1}`) contributes no segment rather than a lie.
      if (typeof key === 'string') path.push(key);
    }
    // `object` contributes nothing: its children are properties, and the property is what
    // carries the name.

    current = child;
  }
}
